import { create } from "zustand";
import { sipEngine } from "../sip/SipEngine";
import { playRingtone, stopRingtone } from "../audio/ringtone";
import {
  acceptCall,
  hangupCall,
  hasWebphoneCredentials,
  makeCall,
  rejectCall,
  sendDtmf,
  setHold,
  setMute,
  setSpeaker,
  startWebphone,
  stopWebphone,
} from "../sip/controls";
import type { CallStatus, RegStatus, WebphoneCredentials } from "../types";

type PhoneState = {
  regStatus: RegStatus;
  callStatus: CallStatus;
  remoteNumber: string;
  activeLine: number | null;
  muted: boolean;
  onHold: boolean;
  speakerOn: boolean;
  elapsed: number;
  dialNumber: string;
  webphone: WebphoneCredentials | null;
  bootstrapped: boolean;
  setDialNumber: (value: string) => void;
  bootstrap: (webphone: WebphoneCredentials | null | undefined) => void;
  teardown: () => Promise<void>;
  makeCall: (number?: string) => void;
  hangup: () => void;
  accept: () => void;
  reject: () => void;
  toggleMute: () => void;
  toggleHold: () => void;
  toggleSpeaker: () => void;
  sendDtmf: (digit: string) => void;
  tick: () => void;
  resetCallUi: () => void;
};

let elapsedTimer: number | null = null;

function clearElapsedTimer() {
  if (elapsedTimer != null) {
    window.clearInterval(elapsedTimer);
    elapsedTimer = null;
  }
}

function startElapsedTimer(get: () => PhoneState, set: (partial: Partial<PhoneState>) => void) {
  clearElapsedTimer();
  elapsedTimer = window.setInterval(() => {
    const { callStatus, onHold } = get();
    if (callStatus !== "connected" || onHold) return;
    set({ elapsed: get().elapsed + 1 });
  }, 1000);
}

function mapRegState(state: string): RegStatus {
  if (state === "registered") return "online";
  if (state === "failed" || state === "unregistered") return "offline";
  return "connecting";
}

function mapCallEvent(event: string): CallStatus | null {
  switch (event) {
    case "incoming":
      return "incoming";
    case "calling":
      return "calling";
    case "ringing":
      return "ringing";
    case "connected":
      return "connected";
    case "disconnected":
      return "idle";
    default:
      return null;
  }
}

export const usePhoneStore = create<PhoneState>((set, get) => ({
  regStatus: "offline",
  callStatus: "idle",
  remoteNumber: "",
  activeLine: null,
  muted: false,
  onHold: false,
  speakerOn: true,
  elapsed: 0,
  dialNumber: "",
  webphone: null,
  bootstrapped: false,

  setDialNumber: (dialNumber) => set({ dialNumber }),

  resetCallUi: () => {
    clearElapsedTimer();
    stopRingtone();
    set({
      callStatus: "idle",
      remoteNumber: "",
      activeLine: null,
      muted: false,
      onHold: false,
      elapsed: 0,
      dialNumber: "",
    });
  },

  bootstrap: (webphone) => {
    const { bootstrapped, teardown } = get();
    if (!hasWebphoneCredentials(webphone)) {
      void teardown();
      set({ webphone: null, regStatus: "offline", bootstrapped: false });
      return;
    }

    if (bootstrapped && get().webphone?.sip_username === webphone!.sip_username) {
      return;
    }

    sipEngine.onRegStateChangeCb = (state) => {
      set({ regStatus: mapRegState(state) });
    };

    sipEngine.onAppStateChangeCb = (state) => {
      if (state === "starting" || state === "registering") {
        set({ regStatus: "connecting" });
      } else if (state === "stopped" || state === "unregistered") {
        set({ regStatus: "offline" });
      }
    };

    sipEngine.onCallStateChangeCb = (event, direction, peer, _display, line) => {
      const lineId = Number(line) || null;

      if (event === "disconnected") {
        get().resetCallUi();
        return;
      }

      let callStatus = mapCallEvent(event);
      // Inbound: SipEngine emits incoming then ringing — keep accept UI until connected.
      if (event === "ringing" && direction === 2) {
        callStatus = "incoming";
      }
      if (!callStatus) return;

      set({
        callStatus,
        remoteNumber: peer || get().remoteNumber,
        activeLine: lineId,
        dialNumber: peer || get().dialNumber,
      });

      if (callStatus === "connected") {
        stopRingtone();
        set({ elapsed: 0 });
        startElapsedTimer(get, set);
      } else if (callStatus === "incoming") {
        playRingtone("incoming");
      } else if (callStatus === "calling" || callStatus === "ringing") {
        playRingtone("outgoing");
      }

      if (callStatus === "incoming" || callStatus === "calling" || callStatus === "ringing") {
        set({ muted: false, onHold: false });
      }
    };

    set({ webphone: webphone!, regStatus: "connecting", bootstrapped: true });
    startWebphone(webphone!);
  },

  teardown: async () => {
    clearElapsedTimer();
    stopRingtone();
    sipEngine.onRegStateChangeCb = null;
    sipEngine.onAppStateChangeCb = null;
    sipEngine.onCallStateChangeCb = null;
    await stopWebphone();
    set({
      regStatus: "offline",
      callStatus: "idle",
      remoteNumber: "",
      activeLine: null,
      muted: false,
      onHold: false,
      elapsed: 0,
      dialNumber: "",
      webphone: null,
      bootstrapped: false,
    });
  },

  makeCall: (number) => {
    const dest = (number ?? get().dialNumber).trim();
    if (!dest) return;

    // Always put the number on the dialer UI first.
    set({ dialNumber: dest, remoteNumber: dest });

    if (get().regStatus !== "online") {
      console.warn("Cannot dial: SIP not registered");
      return;
    }
    playRingtone("outgoing");
    set({
      callStatus: "calling",
      muted: false,
      onHold: false,
      elapsed: 0,
    });
    makeCall(dest);
  },

  hangup: () => {
    const line = get().activeLine ?? undefined;
    hangupCall(line);
    get().resetCallUi();
  },

  accept: () => {
    stopRingtone();
    const line = get().activeLine ?? undefined;
    acceptCall(line);
  },

  reject: () => {
    const line = get().activeLine ?? undefined;
    rejectCall(line);
    get().resetCallUi();
  },

  toggleMute: () => {
    const next = !get().muted;
    setMute(next);
    set({ muted: next });
  },

  toggleHold: () => {
    const next = !get().onHold;
    setHold(next, get().activeLine ?? undefined);
    set({ onHold: next });
  },

  toggleSpeaker: () => {
    const next = !get().speakerOn;
    setSpeaker(next);
    set({ speakerOn: next });
  },

  sendDtmf: (digit) => {
    sendDtmf(digit);
  },

  tick: () => {
    if (get().callStatus === "connected" && !get().onHold) {
      set({ elapsed: get().elapsed + 1 });
    }
  },
}));
